/**
 * Scroll Hint Component
 *
 * Standalone page-level scroll encouragement indicator.
 * Shows a subtle animated chevron at the bottom of the viewport
 * when the page has content below the fold.
 *
 * Behavior:
 * - Appears on page load if user is near the top and content exists below
 * - Scroll down (>80px) → fades out (temporary)
 * - Scroll back to top (≤30px) → fades back in (always)
 * - Click → smooth-scrolls past the fold, hides (reappears on scroll to top)
 * - Auto-fade after 10s (temporary, reappears on scroll to top)
 * - Never shows over a link or button of the page
 *
 * All hide actions are temporary. The chevron always reappears when
 * the user scrolls back to the top of the page.
 *
 * Visibility is CSS-only (opacity + pointer-events via .is-visible class).
 * No hidden attribute, no transitionend — eliminates race conditions.
 *
 * Heritage 4.1.4 compatibility: on desktop (≥990 px) the theme wraps all
 * page content in a `.page-wrapper` element that carries `height: 100dvh;
 * overflow-y: auto`, making it the scroll container instead of the window.
 * On mobile the same element is in the DOM but has no height cap, so its
 * scrollHeight equals its clientHeight and the window remains the scroll
 * container. The predicate `wrapper.scrollHeight > wrapper.clientHeight`
 * resolves the real scroll container at init time without relying on
 * computed styles (which `html[scroll-lock]` could temporarily flip to
 * `overflow: hidden`, misleading a style-based test).
 *
 * @see sections/scroll-hint-mab.liquid
 */
class ScrollHintComponent extends HTMLElement {
  constructor() {
    super();
    this._active = false;
    /** @type {Element|Window} */
    this._scroller = window;
  }

  connectedCallback() {
    requestAnimationFrame(() => this.init());
  }

  disconnectedCallback() {
    this.destroyListeners();
  }

  /**
   * Returns scroll position from the top of the scroll container.
   * @returns {number}
   */
  _scrollTop() {
    return this._scroller === window
      ? window.scrollY
      : /** @type {Element} */ (this._scroller).scrollTop;
  }

  /**
   * Returns the full scrollable height of the scroll container.
   * @returns {number}
   */
  _scrollHeight() {
    return this._scroller === window
      ? document.documentElement.scrollHeight
      : /** @type {Element} */ (this._scroller).scrollHeight;
  }

  /**
   * Returns the visible height of the scroll container.
   * @returns {number}
   */
  _clientHeight() {
    return this._scroller === window
      ? window.innerHeight
      : /** @type {Element} */ (this._scroller).clientHeight;
  }

  init() {
    if (this._active) return;

    const hint = this.querySelector('[data-scroll-hint]');
    if (!hint) return;

    // Heritage 4.1.4 routes page scroll through .page-wrapper on desktop
    // (height: 100dvh; overflow-y: auto). On mobile the element is in the DOM
    // but does not scroll — window does. Check whether the wrapper actually
    // overflows its visible height rather than testing its mere existence or
    // computed styles (scroll-lock sets overflow: hidden temporarily, which
    // would mislead a style-based check).
    const wrapper = document.querySelector('.page-wrapper');
    this._scroller = wrapper && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : window;

    // Only show if user is still near the top
    if (this._scrollTop() > this._clientHeight() * 0.5) return;

    // Only show if page has content below the fold
    if (this._scrollHeight() <= this._clientHeight() * 1.2) return;

    this.show(hint);

    // Click → smooth-scroll to named section anchor or past the fold
    const targetKey = this.dataset.scrollTarget;
    const targetEl = targetKey ? document.getElementById(targetKey) : null;

    hint.addEventListener('click', () => {
      if (targetEl) {
        const offset = 88;
        this._scroller.scrollTo({
          top: targetEl.getBoundingClientRect().top + this._scrollTop() - offset,
          behavior: 'smooth'
        });
      } else {
        this._scroller.scrollTo({ top: this._clientHeight() * 0.85, behavior: 'smooth' });
      }
      this.hide();
    });

    // Bidirectional scroll: hide on scroll down, re-show on scroll back to top
    this._scrollHandler = () => {
      const top = this._scrollTop();
      if (top > 80 && this._active) {
        this.hide();
      } else if (top <= 30 && !this._active) {
        const h = this.querySelector('[data-scroll-hint]');
        if (h) this.show(h);
      }
    };
    this._scroller.addEventListener('scroll', this._scrollHandler, { passive: true });

    // Auto-fade after 10s (temporary — reappears on scroll to top)
    this._timeout = setTimeout(() => this.hide(), 10000);
  }

  /**
   * Show the hint (add CSS class for opacity + pointer-events).
   * @param {HTMLElement} hint
   */
  show(hint) {
    if (this._overlapsControl(hint)) return;
    this._active = true;
    hint.classList.add('is-visible');
    hint.removeAttribute('aria-hidden');
  }

  /**
   * Whether the hint, where it is pinned, would sit over a visible link or button of the page:
   * on a short viewport the fold can end on a call to action.
   * @param {HTMLElement} hint
   * @returns {boolean}
   */
  _overlapsControl(hint) {
    const box = hint.getBoundingClientRect();
    for (const control of document.querySelectorAll('main a, main button')) {
      if (control === hint || hint.contains(control)) continue;
      const rect = control.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      const overlaps =
        rect.left < box.right && rect.right > box.left && rect.top < box.bottom && rect.bottom > box.top;
      if (overlaps) return true;
    }
    return false;
  }

  /**
   * Hide the hint (remove CSS class, always reversible).
   */
  hide() {
    const hint = this.querySelector('[data-scroll-hint]');
    if (!hint) return;
    this._active = false;
    hint.classList.remove('is-visible');
    hint.setAttribute('aria-hidden', 'true');
  }

  /**
   * Clean up timers and listeners.
   */
  destroyListeners() {
    clearTimeout(this._timeout);
    if (this._scrollHandler) {
      this._scroller.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }
  }
}

if (!customElements.get('scroll-hint-component')) {
  customElements.define('scroll-hint-component', ScrollHintComponent);
}
